import React from 'react';
import {IAsyncUploaderQueuedFile} from '@src/@interfaces/services/Http/IAsyncUploaderQueuedFile';
import {Events, IAsyncUploader} from '@src/@interfaces/services/Http/IAsyncUploader';
import {IListener, ISubscription} from '@src/@interfaces/services/Events';
import {IAsyncUploaderFileMeta} from '@src/@interfaces/services/Http/IAsyncUploaderFileMeta';
import app from '@src/helpers/app';

type TAsyncFileUploaderContext = {
    getUploader(): IAsyncUploader,
    getItems(): IAsyncUploaderQueuedFile[],
    getItem(uniqueId: string): IAsyncUploaderQueuedFile | null,
    getItemByServerHash(serverHash: string): IAsyncUploaderQueuedFile | null,
    getItemsToLoad(): IAsyncUploaderQueuedFile[],
    getLoadingNowItems(): IAsyncUploaderQueuedFile[],
    push(file: File, context?: IAsyncUploaderFileMeta['context']): IAsyncUploaderQueuedFile,
    remove(uniqueId: string): boolean,
    pause(uniqueId: string): boolean,
    resume(uniqueId: string): boolean,
    getProgress(): number;
    getNumberOfParallelUploads(): number,
    setNumberOfParallelUploads(number: number): void,
    listen<TPayload>(event: Events | Events[], listener: IListener<TPayload>): ISubscription,
};

export const defaultContextValue: TAsyncFileUploaderContext = {
    getUploader(): IAsyncUploader {
        return app('async-uploader');
    },

    getItems(): IAsyncUploaderQueuedFile[] {
        return app('async-uploader').getItems();
    },

    getItem(uniqueId: string): IAsyncUploaderQueuedFile | null {
        return app('async-uploader').getItem(uniqueId);
    },

    getItemByServerHash(serverHash: string): IAsyncUploaderQueuedFile | null {
        return app('async-uploader').getItemByServerHash(serverHash);
    },

    getItemsToLoad(): IAsyncUploaderQueuedFile[] {
        return app('async-uploader').getItemsToLoad();
    },

    getLoadingNowItems(): IAsyncUploaderQueuedFile[] {
        return app('async-uploader').getLoadingNowItems();
    },

    push(file: File, context?: IAsyncUploaderFileMeta['context']): IAsyncUploaderQueuedFile {
        return app('async-uploader').push(file, context);
    },

    remove(uniqueId: string): boolean {
        return app('async-uploader').remove(uniqueId);
    },

    pause(uniqueId: string): boolean {
        return app('async-uploader').pause(uniqueId);
    },

    resume(uniqueId: string): boolean {
        return app('async-uploader').resume(uniqueId);
    },

    getProgress(): number {
        return app('async-uploader').getProgress();
    },

    getNumberOfParallelUploads(): number {
        return app('async-uploader').getMaxNumberOfParallelUploads();
    },

    setNumberOfParallelUploads(number: number): void {
        return app('async-uploader').setMaxNumberOfParallelUploads(number);
    },

    listen<TPayload>(event: Events | Events[], listener: IListener<TPayload>): ISubscription {
        return app('async-uploader').listen<TPayload>(event, listener);
    },

};

export const AsyncFileUploaderContext = React.createContext<TAsyncFileUploaderContext>(defaultContextValue);
